import {
  IWebchatSettings,
  TWebchatExtraParams,
  TWebchatWidgetFailureReason,
  TWebchatWidgetStatus,
} from 'modules/webchat/domain/entities';
import {
  IWebchatWidgetExitAction,
  IWebchatWidgetMountOptions,
  IWebchatWidgetProvider,
} from 'modules/webchat/domain/providers';

const LOADER_BASE_URL = 'https://dash.callbell.eu/include/livechat';
const LOADER_SCRIPT_ID = 'callbell-livechat-loader';
const HOST_ID = 'callbell-livechat-host';
/**
 * O widget pede aqui o token da sala, mandando o cookie da Callbell. O site
 * intercepta essa chamada (o bundle usa o fetch global da página) e entrega o
 * token da sala escolhida por pessoa; se não conseguir, deixa a chamada
 * original seguir, e o widget fica com a sala do cookie.
 */
const TOKEN_ENDPOINT = 'https://livechat-ingest.callbell.eu/token';

/**
 * DOM interno do bundle livechat.callbell.eu/main.js, sem documentação: o
 * widget não expõe API JS para abrir o chat nem eventos, só o clique no
 * launcher dentro do shadow root aberto do host, e a janela aberta aparece
 * como #callbell-livechat-chat-container. O launcher só é desenhado depois
 * que o POST /token devolve a sala. Pode mudar a qualquer deploy da
 * Callbell; aqui é o único lugar que depende disso.
 */
const LAUNCHER_SELECTOR = 'aside';
const CHAT_CONTAINER_ID = 'callbell-livechat-chat-container';
/**
 * Tela de boas-vindas do widget ("Qual é o seu nome?"): o que a pessoa digita
 * ali vira, de uma vez, a primeira mensagem e o nome do contato na Callbell.
 * Como o site já sabe o nome (pré-chat ou sessão), ele responde essa tela
 * sozinho. Numa conversa já existente o widget abre direto em .chat-view, e
 * nada é enviado. Sem agente disponível não há textarea, e nada é enviado.
 */
const WELCOME_INPUT_SELECTOR = 'form textarea';
const CHAT_VIEW_SELECTOR = '.chat-view';
/** Cabeçalho da conversa aberta (a tela de boas-vindas tem outro .chat-header, fora de .chat-view). */
const CHAT_HEADER_SELECTOR = `${CHAT_VIEW_SELECTOR} .chat-header`;
const EXIT_BAR_CLASS = 'vialaser-webchat-bar';
const EXIT_PERSON_CLASS = 'vialaser-webchat-person';
const EXIT_BUTTON_CLASS = 'vialaser-webchat-exit';
/** Cor do cabeçalho do canal na Callbell, usada no texto do botão quando o DOM não informa. */
const FALLBACK_HEADER_COLOR = '#43b899';
/**
 * Estilos inline porque a faixa vive dentro do shadow root do widget, fora do
 * CSS do site. Ela ocupa o cabeçalho vazio da conversa: quem está falando à
 * esquerda e a pílula de sair à direita (botão pílula do design system).
 */
const EXIT_BAR_STYLE = [
  'position:absolute',
  'left:24px',
  'right:20px',
  'top:50%',
  'transform:translateY(-50%)',
  'display:flex',
  'align-items:center',
  'justify-content:space-between',
  'gap:12px',
  'color:#fff',
  'font-family:inherit',
  'z-index:1',
].join(';');
const EXIT_PERSON_STYLE = [
  'font-size:13px',
  'line-height:1.3',
  'opacity:0.95',
  'overflow:hidden',
  'text-overflow:ellipsis',
  'white-space:nowrap',
].join(';');
const EXIT_BUTTON_STYLE = [
  'flex:none',
  'display:inline-flex',
  'align-items:center',
  'gap:6px',
  'height:30px',
  'padding:0 12px 0 10px',
  'border:0',
  'border-radius:999px',
  'background:#fff',
  'font-family:inherit',
  'font-size:12px',
  'font-weight:700',
  'line-height:1',
  'cursor:pointer',
  'box-shadow:0 2px 6px rgba(0,0,0,0.12)',
].join(';');
/** Ícone log-out do Lucide, o conjunto de ícones do design system. */
const EXIT_ICON_SVG =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>';
/** Prazo, contado do clique no launcher, para a janela do chat aparecer. */
const INTRODUCTION_TIMEOUT_MS = 3000;
const INTRODUCTION_POLL_INTERVAL_MS = 100;
const INTRODUCTION_MAX_ATTEMPTS =
  INTRODUCTION_TIMEOUT_MS / INTRODUCTION_POLL_INTERVAL_MS;

const IDLE_TIMEOUT_MS = 3000;
const IDLE_FALLBACK_DELAY_MS = 1500;
/**
 * Prazo, contado da injeção do loader, para o launcher aparecer: loader,
 * main.js (cerca de 440 KB) e POST /token.
 */
const READY_TIMEOUT_MS = 15000;
const READY_POLL_INTERVAL_MS = 250;
const READY_MAX_ATTEMPTS = READY_TIMEOUT_MS / READY_POLL_INTERVAL_MS;
/** Limite do open(): espera por ocioso mais o prazo do launcher, com folga. */
const OPEN_TIMEOUT_MS = IDLE_TIMEOUT_MS + READY_TIMEOUT_MS + 2000;

interface ICallbellSettings {
  uuid: string;
  script_token: string;
  extra_params: TWebchatExtraParams;
}

declare global {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- augmentation da interface global do browser
  interface Window {
    callbellSettings?: ICallbellSettings;
  }
}

export const buildCallbellLoaderUrl = ({
  uuid,
  scriptToken,
}: IWebchatSettings) =>
  `${LOADER_BASE_URL}/${encodeURIComponent(scriptToken)}/${encodeURIComponent(
    uuid,
  )}.js`;

const runWhenBrowserIsIdle = (task: () => void) => {
  const schedule = () => {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(() => task(), { timeout: IDLE_TIMEOUT_MS });

      return;
    }

    window.setTimeout(task, IDLE_FALLBACK_DELAY_MS);
  };

  if (document.readyState === 'complete') {
    schedule();

    return;
  }

  window.addEventListener('load', schedule, { once: true });
};

/**
 * O textarea do widget é controlado pelo React dele: só o setter nativo
 * seguido de um evento input atualiza o estado do widget.
 */
const fillControlledTextarea = (
  textarea: HTMLTextAreaElement,
  value: string,
) => {
  const nativeValueSetter = Object.getOwnPropertyDescriptor(
    HTMLTextAreaElement.prototype,
    'value',
  )?.set;

  if (nativeValueSetter) {
    nativeValueSetter.call(textarea, value);
  } else {
    textarea.value = value;
  }

  textarea.dispatchEvent(new Event('input', { bubbles: true }));
};

const readRequestUrl = (input: RequestInfo | URL) => {
  if (typeof input === 'string') return input;

  return input instanceof URL ? input.href : input.url;
};

export default class CallbellWebchatWidgetProvider
  implements IWebchatWidgetProvider
{
  private status: TWebchatWidgetStatus = 'idle';

  private failureReason: TWebchatWidgetFailureReason | null = null;

  private visible = true;

  /** Próxima checagem do launcher; null quando nenhuma está agendada. */
  private launcherCheckId: number | null = null;

  /** Nome com que o site responde a tela de boas-vindas; null quando não há nome. */
  private introductionName: string | null = null;

  /** Próxima checagem da janela do chat para a apresentação; null quando nenhuma está agendada. */
  private introductionCheckId: number | null = null;

  /** Token da sala escolhida pelo site; null para deixar o widget com o cookie da Callbell. */
  private issueToken: (() => Promise<string>) | null = null;

  /** fetch da página antes da interceptação; null enquanto ela não está instalada. */
  private originalFetch: typeof window.fetch | null = null;

  private exitAction: IWebchatWidgetExitAction | null = null;

  /** Observa a janela do chat para desenhar o botão de sair quando a conversa aparece. */
  private chatWindowObserver: MutationObserver | null = null;

  private openListeners = new Set<() => void>();

  private statusListeners = new Set<(status: TWebchatWidgetStatus) => void>();

  public mount(
    settings: IWebchatSettings,
    extraParams: TWebchatExtraParams,
    options: IWebchatWidgetMountOptions = {},
  ) {
    if (this.status !== 'idle' || document.getElementById(LOADER_SCRIPT_ID)) {
      return;
    }

    this.setStatus('loading');
    this.introductionName = extraParams.nome?.trim() || null;
    this.issueToken = options.issueToken ?? null;

    runWhenBrowserIsIdle(() => this.injectLoader(settings, extraParams));
  }

  public setExitAction(action: IWebchatWidgetExitAction | null) {
    this.exitAction = action;
    this.renderExitButton();
  }

  public getStatus() {
    return this.status;
  }

  public getFailureReason() {
    return this.failureReason;
  }

  public onStatusChange(listener: (status: TWebchatWidgetStatus) => void) {
    this.statusListeners.add(listener);

    return () => {
      this.statusListeners.delete(listener);
    };
  }

  public setVisible(visible: boolean) {
    this.visible = visible;
    this.applyVisibility();
  }

  public reset() {
    this.stopWaitingForLauncher();
    this.stopIntroduction();
    this.stopObservingChatWindow();
    this.uninstallTokenInterceptor();
    // O bundle lê callbellSettings.extra_params uma única vez, na montagem: trocar a identidade exige recarregar a página.
    window.setTimeout(() => window.location.reload(), 0);
  }

  public open(): Promise<boolean> {
    if (this.status !== 'loading') {
      return Promise.resolve(this.clickLauncher());
    }

    return new Promise(resolve => {
      let timeoutId = 0;

      const unsubscribe = this.onStatusChange(() => {
        window.clearTimeout(timeoutId);
        unsubscribe();
        resolve(this.clickLauncher());
      });

      timeoutId = window.setTimeout(() => {
        unsubscribe();
        resolve(false);
      }, OPEN_TIMEOUT_MS);
    });
  }

  public onOpen(listener: () => void) {
    this.openListeners.add(listener);

    return () => {
      this.openListeners.delete(listener);
    };
  }

  private injectLoader(
    settings: IWebchatSettings,
    extraParams: TWebchatExtraParams,
  ) {
    window.callbellSettings = {
      uuid: settings.uuid,
      script_token: settings.scriptToken,
      extra_params: extraParams,
    };

    // Antes do bundle: o POST /token acontece assim que o main.js roda.
    if (this.issueToken) this.installTokenInterceptor(this.issueToken);

    const script = document.createElement('script');

    script.id = LOADER_SCRIPT_ID;
    script.async = true;
    script.src = buildCallbellLoaderUrl(settings);
    script.onload = () => this.handleLoaderReady();
    script.onerror = () => this.fail('LOADER_ERROR');

    document.body.appendChild(script);

    this.waitForLauncher(1);
  }

  private installTokenInterceptor(issueToken: () => Promise<string>) {
    if (this.originalFetch) return;

    const originalFetch = window.fetch;

    this.originalFetch = originalFetch;

    window.fetch = (input, init) => {
      if (readRequestUrl(input) !== TOKEN_ENDPOINT) {
        return originalFetch.call(window, input, init);
      }

      return (
        issueToken()
          .then(
            token =>
              new Response(JSON.stringify({ token }), {
                status: 200,
                headers: { 'Content-Type': 'application/json' },
              }),
          )
          // Sem o token do site, o widget segue com a sala do cookie da Callbell (por navegador).
          .catch(() => originalFetch.call(window, input, init))
      );
    };
  }

  private uninstallTokenInterceptor() {
    if (!this.originalFetch) return;

    window.fetch = this.originalFetch;
    this.originalFetch = null;
  }

  private handleLoaderReady() {
    const host = document.getElementById(HOST_ID);

    // O loader cria o host ao executar; sem ele, caiu no ramo de erro (token, uuid ou domínio).
    if (!host) {
      this.fail('HOST_NOT_CREATED');

      return;
    }

    // A rota pode ter mudado enquanto o loader carregava.
    this.applyVisibility();
    // Captura no host: roda antes do clique chegar ao widget e trocar o estado.
    host.addEventListener('click', this.handleHostClick, true);
  }

  private waitForLauncher(attempt: number) {
    this.launcherCheckId = null;

    if (this.status !== 'loading') return;

    if (this.getLauncher()) {
      this.setStatus('ready');
      // Com o launcher, o shadow root existe: dá para acompanhar a janela do chat.
      this.observeChatWindow();

      return;
    }

    if (attempt >= READY_MAX_ATTEMPTS) {
      this.fail(
        document.getElementById(HOST_ID)
          ? 'LAUNCHER_TIMEOUT'
          : 'HOST_NOT_CREATED',
      );

      return;
    }

    this.launcherCheckId = window.setTimeout(
      () => this.waitForLauncher(attempt + 1),
      READY_POLL_INTERVAL_MS,
    );
  }

  private stopWaitingForLauncher() {
    if (this.launcherCheckId === null) return;

    window.clearTimeout(this.launcherCheckId);
    this.launcherCheckId = null;
  }

  private fail(reason: TWebchatWidgetFailureReason) {
    if (this.status !== 'loading') return;

    this.failureReason = reason;
    this.setStatus('failed');
    // Um widget que aparecer depois do prazo fica escondido: o site já mostrou a alternativa.
    this.applyVisibility();
  }

  private setStatus(status: TWebchatWidgetStatus) {
    // Pronto ou falhou, por qualquer caminho: não há mais launcher para esperar.
    if (status !== 'loading') this.stopWaitingForLauncher();

    this.status = status;
    this.statusListeners.forEach(listener => listener(status));
  }

  private clickLauncher() {
    if (!this.visible || this.status !== 'ready') return false;

    if (this.isChatOpen()) return true;

    const launcher = this.getLauncher();

    if (!launcher) return false;

    launcher.click();
    // Com o listener do host no lugar, o click acima já agendou a apresentação.
    this.introduceVisitor();

    return true;
  }

  private handleHostClick = (event: Event) => {
    const isLauncherClick = event
      .composedPath()
      .some(
        target =>
          target instanceof Element && target.matches(LAUNCHER_SELECTOR),
      );

    if (!isLauncherClick || this.isChatOpen()) return;

    this.openListeners.forEach(listener => listener());
    this.introduceVisitor();
  };

  /**
   * Espera a janela do chat aparecer depois do clique no launcher e, se ela
   * abriu na tela de boas-vindas, responde com o nome. Uma só espera por vez.
   */
  private introduceVisitor(attempt = 1) {
    if (attempt === 1 && this.introductionCheckId !== null) return;

    this.introductionCheckId = null;

    if (!this.introductionName) return;

    const container = this.getChatContainer();

    // A janela pode aparecer antes do conteúdo: segue esperando até ter tela de boas-vindas ou conversa.
    if (
      container &&
      this.answerWelcomeScreen(container, this.introductionName)
    ) {
      return;
    }

    if (attempt >= INTRODUCTION_MAX_ATTEMPTS) return;

    this.introductionCheckId = window.setTimeout(
      () => this.introduceVisitor(attempt + 1),
      INTRODUCTION_POLL_INTERVAL_MS,
    );
  }

  /** Devolve false só enquanto a janela ainda não tem conteúdo. */
  private answerWelcomeScreen(container: HTMLElement, name: string) {
    if (container.querySelector(CHAT_VIEW_SELECTOR)) return true;

    const textarea = container.querySelector<HTMLTextAreaElement>(
      WELCOME_INPUT_SELECTOR,
    );

    if (!textarea?.form) return false;

    if (!textarea.value.trim()) {
      fillControlledTextarea(textarea, name);
      textarea.form.requestSubmit();
    }

    return true;
  }

  private stopIntroduction() {
    if (this.introductionCheckId === null) return;

    window.clearTimeout(this.introductionCheckId);
    this.introductionCheckId = null;
  }

  private observeChatWindow() {
    const shadowRoot = this.getShadowRoot();

    if (!shadowRoot || this.chatWindowObserver) return;

    this.chatWindowObserver = new MutationObserver(() =>
      this.renderExitButton(),
    );
    this.chatWindowObserver.observe(shadowRoot, {
      childList: true,
      subtree: true,
    });
    this.renderExitButton();
  }

  private stopObservingChatWindow() {
    this.chatWindowObserver?.disconnect();
    this.chatWindowObserver = null;
  }

  /**
   * Mantém a faixa de sair no cabeçalho da conversa aberta: cria quando a
   * conversa aparece, remove quando a ação some. Só escreve no DOM quando há
   * diferença, porque o observer reage às próprias escritas.
   */
  private renderExitButton() {
    const shadowRoot = this.getShadowRoot();

    if (!shadowRoot) return;

    const header = shadowRoot.querySelector<HTMLElement>(CHAT_HEADER_SELECTOR);
    const existing = shadowRoot.querySelector<HTMLElement>(
      `.${EXIT_BAR_CLASS}`,
    );

    if (!header || !this.exitAction) {
      existing?.remove();

      return;
    }

    const bar = existing ?? this.createExitBar();
    const signature = JSON.stringify([
      this.exitAction.personLabel ?? '',
      this.exitAction.personName ?? '',
      this.exitAction.label,
      this.exitAction.title ?? '',
    ]);

    if (bar.dataset.signature !== signature) {
      this.fillExitBar(bar, header);
      bar.dataset.signature = signature;
    }

    if (bar.parentElement !== header) header.appendChild(bar);
  }

  private createExitBar() {
    const bar = document.createElement('div');

    bar.className = EXIT_BAR_CLASS;
    bar.setAttribute('style', EXIT_BAR_STYLE);

    return bar;
  }

  private fillExitBar(bar: HTMLElement, header: HTMLElement) {
    if (!this.exitAction) return;

    const { label, title, personLabel, personName } = this.exitAction;

    bar.textContent = '';

    if (personName) {
      const person = document.createElement('span');
      const name = document.createElement('strong');

      person.className = EXIT_PERSON_CLASS;
      person.setAttribute('style', EXIT_PERSON_STYLE);
      name.style.fontWeight = '700';
      name.textContent = personName;
      if (personLabel) person.append(`${personLabel} `);
      person.appendChild(name);
      bar.appendChild(person);
    } else {
      // Sem nome, a pílula fica sozinha à direita.
      bar.style.justifyContent = 'flex-end';
    }

    const button = document.createElement('button');
    const text = document.createElement('span');

    button.type = 'button';
    button.className = EXIT_BUTTON_CLASS;
    button.setAttribute('style', EXIT_BUTTON_STYLE);
    // O texto do botão fica na cor do cabeçalho do canal, para a pílula branca parecer parte dele.
    button.style.color = this.readHeaderColor(header);
    if (title) button.title = title;
    button.innerHTML = EXIT_ICON_SVG;
    text.textContent = label;
    button.appendChild(text);
    button.addEventListener('click', event => {
      // O clique não é do widget: não deixa o cabeçalho tratá-lo.
      event.stopPropagation();
      this.exitAction?.onSelect();
    });
    bar.appendChild(button);
  }

  private readHeaderColor(header: HTMLElement) {
    const color = window.getComputedStyle(header).backgroundColor;

    return color && color !== 'rgba(0, 0, 0, 0)' && color !== 'transparent'
      ? color
      : FALLBACK_HEADER_COLOR;
  }

  private getShadowRoot() {
    return document.getElementById(HOST_ID)?.shadowRoot ?? null;
  }

  private getLauncher() {
    return (
      this.getShadowRoot()?.querySelector<HTMLElement>(LAUNCHER_SELECTOR) ??
      null
    );
  }

  private getChatContainer() {
    return this.getShadowRoot()?.getElementById(CHAT_CONTAINER_ID) ?? null;
  }

  private isChatOpen() {
    return this.getChatContainer() !== null;
  }

  private applyVisibility() {
    const host = document.getElementById(HOST_ID);

    if (host) {
      host.style.display =
        this.visible && this.status !== 'failed' ? '' : 'none';
    }
  }
}
